import { ApolloClient, useApolloClient } from '@apollo/client';
import { QueryClient, useQuery as useReactQuery } from '@tanstack/react-query';
import {
  generateEnrichedQuery,
  generateEnrichedQueryByUrn,
} from '../utils/dynamicQuery.utils';
import { get, uniq, values } from 'lodash';
import { useApi } from '@backstage/core-plugin-api';
import {
  ConfigurationApi,
  configurationApiRef,
} from '@agilelab/plugin-wb-configuration-common';
import { ConfigSchema } from '@agilelab/plugin-wb-marketplace-common';
import { useMemo } from 'react';

export type MarketplaceConfigurationType =
  ConfigSchema['fe']['displayNameDescriptorField'];

export const QUERY_KEY = ['marketplaceDisplayNameField'];

const STALE_TIME = 1000 * 60 * 5;

const queryFn = (wbConfigApi: ConfigurationApi) => async () => {
  return wbConfigApi.get<MarketplaceConfigurationType>({
    namespace: 'marketplace',
    key: 'fe.displayNameDescriptorField',
  });
};

export const getDisplayNameConfig = (
  wbConfigApi: ConfigurationApi,
  queryClient: QueryClient,
  noCache?: boolean,
) => {
  return queryClient.fetchQuery({
    queryKey: QUERY_KEY,
    queryFn: queryFn(wbConfigApi),
    staleTime: noCache ? 0 : STALE_TIME,
  });
};

export const useDisplayNameConfig = (noCache?: boolean) => {
  const wbConfigApi = useApi(configurationApiRef);

  const { data } = useReactQuery(QUERY_KEY, queryFn(wbConfigApi), {
    staleTime: noCache ? 0 : STALE_TIME,
  });

  return data;
};

function sanitizeKey(key: string): string {
  return key.replace(/[.\(\)\[\]\{\}]/g, '_');
}

const getDisplayNameFromDescriptor = (
  descriptor: Record<string, any>,
  config: MarketplaceConfigurationType,
  sanitize = false,
): string => {
  const parseValue = (field: string) => {
    return get(descriptor, field);
  };
  const kind = descriptor.kind;
  const key = config.types?.[kind] || config.default;
  const titleField = sanitize ? sanitizeKey(key) : key;

  return (
    parseValue(titleField) ??
    parseValue(config.default) ??
    descriptor.fullyQualifiedName ??
    descriptor.name
  );
};

export const useDisplayName = (descriptor?: Record<string, any>): string => {
  const configuration = useDisplayNameConfig();
  if (!descriptor) return '';

  if (!configuration) return descriptor.name;

  return getDisplayNameFromDescriptor(descriptor, configuration);
};

export const getDisplayNameByDescriptor = (
  configuration: MarketplaceConfigurationType,
  descriptor?: Record<string, any>,
) => {
  if (!descriptor) return '';
  return getDisplayNameFromDescriptor(descriptor, configuration);
};

const buildQueryFieldsByConfiguration = (
  configuration: MarketplaceConfigurationType,
) => {
  const configurationFields = uniq([
    configuration.default,
    ...values(configuration.types),
  ]);

  const queryFields = [
    'kind',
    'fullyQualifiedName',
    'name',
    ...configurationFields,
  ];

  return queryFields.map(f => ({ key: sanitizeKey(f), path: f }));
};

export const getDisplayNameMap = async (
  ids: string[],
  apolloClient: ApolloClient<any>,
  configuration: MarketplaceConfigurationType,
): Promise<Map<string, string>> => {
  const resultMap = new Map<string, string>();

  const { data } = await apolloClient.query({
    query: generateEnrichedQuery(
      buildQueryFieldsByConfiguration(configuration),
    ),
    variables: { ids },
    // this must be added to prevent dynamically generated query to cause cache issues
    fetchPolicy: 'no-cache',
  });

  const records = data?.dataProductInstance ?? [];

  for (const item of records) {
    const displayName = getDisplayNameFromDescriptor(item, configuration, true);
    resultMap.set(item.entityId.toString(), displayName);
  }

  return resultMap;
};

/**
 * Like getDisplayNameMap but looks up by external_id (URN) + environment instead of numeric id.
 */
export const getDisplayNameMapByUrn = async (
  urns: string[],
  environmentId: number,
  apolloClient: ApolloClient<any>,
  configuration: MarketplaceConfigurationType,
): Promise<Map<string, string>> => {
  const resultMap = new Map<string, string>();

  const uniqueIds = [...new Set(urns)];

  const { data } = await apolloClient.query({
    query: generateEnrichedQueryByUrn(
      buildQueryFieldsByConfiguration(configuration),
    ),
    variables: { urns: uniqueIds, environmentId: environmentId },
    fetchPolicy: 'no-cache',
  });

  const records = data?.marketplace_instances ?? [];

  for (const item of records) {
    const displayName = getDisplayNameFromDescriptor(item, configuration, true);
    resultMap.set(item.externalId, displayName);
  }

  return resultMap;
};

const EMPTY_DISPLAY_NAME_MAP = new Map<string, string>();

/**
 * Resolves display names for marketplace instances, using urn + environment
 */
export function useDisplayNamesByUrns(
  urns: string[],
  environmentId: number,
  options?: { enabled?: boolean },
) {
  const apolloClient = useApolloClient();
  const configuration = useDisplayNameConfig();

  const enabled = options?.enabled ?? true;

  const uniqueUrns = useMemo(() => [...new Set(urns)], [urns]);

  const result = useReactQuery({
    queryKey: ['marketplace-display-names-by-urns', uniqueUrns, environmentId],
    queryFn: () =>
      uniqueUrns.length > 0
        ? getDisplayNameMapByUrn(
            uniqueUrns,
            environmentId,
            apolloClient,
            configuration!,
          )
        : EMPTY_DISPLAY_NAME_MAP,
    enabled: Boolean(configuration) && enabled,
    staleTime: 1000 * 60 * 5,
  });

  return result;
}
